import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
} from "@mui/material";
import { useFormik } from "formik";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCustomerSchema } from "./schema";
import { FormikSelect } from "../../../../components/Controls/Formik/Select";
import { FormikSubmitButton } from "../../../../components/Controls/Formik/SubmitButton";
import { FormikTextField } from "../../../../components/Controls/Formik/TextField";
import { useFormTranslator } from "../../../../hooks/useFormTranslator";
import {
    CustomerEdit,
    CustomerType,
} from "../../../../models/customer/Customer";
import { EditCustomerViewCallback } from "../../../../service/CustomerService";

interface EditCustomerDialogProps {
    customer: CustomerEdit;
    editCustomer: EditCustomerViewCallback;
}

export function EditCustomerDialog({
    customer,
    editCustomer,
}: EditCustomerDialogProps) {
    const { t } = useTranslation();
    const translator = useFormTranslator("customer");

    const { validationSchema, isRequired } = useCustomerSchema(
        "edit",
        translator,
    );

    const [opened, setOpened] = useState(false);

    const onOpen = useCallback(() => setOpened(true), [setOpened]);
    const onClose = useCallback(() => setOpened(false), [setOpened]);

    const onSubmit = useCallback(
        async (values: CustomerEdit) => {
            await editCustomer(values);
            onClose();
        },
        [editCustomer, onClose],
    );

    const formik = useFormik({
        initialValues: customer,
        validationSchema,
        onSubmit,
    });

    return (
        <>
            <Button onClick={onOpen} variant="outlined">
                {t("common.edit", { resource: t("pages.customer.resource") })}
            </Button>
            <Dialog open={opened} fullWidth>
                <DialogTitle>
                    {t("common.edit", {
                        resource: t("pages.customer.resource"),
                    })}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} marginY={1}>
                        <FormikSelect
                            formik={formik}
                            translator={translator}
                            field="type"
                            required
                            fullWidth
                        >
                            {Object.keys(CustomerType).map(key => (
                                <MenuItem
                                    key={`customer_type${key}`}
                                    value={key}
                                >
                                    {translator.getField("type").getEnum(key)}
                                </MenuItem>
                            ))}
                        </FormikSelect>

                        <FormikTextField
                            formik={formik}
                            translator={translator}
                            field="name"
                            required={isRequired("name")}
                            fullWidth
                        />

                        <FormikTextField
                            formik={formik}
                            translator={translator}
                            field="email"
                            required={isRequired("email")}
                            fullWidth
                        />

                        <FormikTextField
                            formik={formik}
                            translator={translator}
                            field="phoneNumber"
                            required={isRequired("phoneNumber")}
                            fullWidth
                        />

                        <FormikTextField
                            formik={formik}
                            translator={translator}
                            field="mobileNumber"
                            required={isRequired("mobileNumber")}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button color="error" variant="contained" onClick={onClose}>
                        {t("common.cancel")}
                    </Button>
                    <FormikSubmitButton formik={formik} />
                </DialogActions>
            </Dialog>
        </>
    );
}
